import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, Building2, Camera, MapPinned, Home as HomeIcon, Activity, DoorOpen, CheckCircle2, ServerCog, ArrowRight } from 'lucide-react';
import { CTAButton, Pill, SectionLabel, fadeUp } from '../components/shared';
import { services, industries, processSteps } from '../data/site';
export function Home() {
  return <>
      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-950 pt-28 text-white" data-id="element-261">
        <div className="absolute left-1/2 top-0 h-[42rem] w-[42rem] -translate-x-1/2 rounded-full bg-sky-500/20 blur-3xl" data-id="element-262" />
        <div className="absolute right-0 top-32 h-96 w-96 rounded-full bg-blue-700/20 blur-3xl" data-id="element-263" />
        <div className="absolute inset-0 opacity-[0.08]" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,.35) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.35) 1px, transparent 1px)',
        backgroundSize: '42px 42px'
      }} data-id="element-264" />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-4 pb-24 pt-16 sm:px-6 lg:grid-cols-[1.02fr_.98fr] lg:px-8 lg:pb-32 lg:pt-24" data-id="element-265">
          <motion.div initial="hidden" animate="visible" transition={{
          staggerChildren: 0.09
        }} data-id="element-266">
            <motion.div variants={fadeUp} data-id="element-267">
              <SectionLabel dark data-id="element-268">Commercial first security</SectionLabel>
            </motion.div>
            <motion.h1 variants={fadeUp} className="max-w-4xl text-5xl font-black leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl" data-id="element-269">
              See More. Control More. Worry Less.
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-7 max-w-2xl text-lg leading-8 text-slate-300 sm:text-xl" data-id="element-270">
              Watchdog Security designs, installs, repairs, and supports smart
              alarm, camera, access control, and automation systems for
              businesses, properties, and homes across Northern California.
            </motion.p>
            <motion.div variants={fadeUp} className="mt-9 flex flex-col gap-3 sm:flex-row" data-id="element-271">
              <CTAButton to="/assessment" data-id="element-272">
                Schedule a Free Security Assessment
              </CTAButton>
              <CTAButton variant="secondary" to="/commercial" data-id="element-273">
                Explore Commercial Solutions
              </CTAButton>
            </motion.div>
            <motion.div variants={fadeUp} className="mt-9 flex flex-wrap gap-3" data-id="element-274">
              <Pill data-id="element-275">Access Control</Pill>
              <Pill data-id="element-276">Video Surveillance</Pill>
              <Pill data-id="element-277">Intrusion Alarms</Pill>
              <Pill data-id="element-278">Alarm.com Powered</Pill>
              <Pill data-id="element-279">Local Installation</Pill>
            </motion.div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.96,
          y: 24
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.2
        }} className="relative" data-id="element-280">
            <div className="absolute -inset-4 rounded-[2rem] bg-sky-400/10 blur-2xl" data-id="element-281" />
            <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.06] p-4 shadow-2xl backdrop-blur-xl" data-id="element-282">
              <div className="rounded-[1.5rem] border border-white/10 bg-slate-900 p-5" data-id="element-283">
                <div className="mb-5 flex items-center justify-between" data-id="element-284">
                  <div data-id="element-285">
                    <p className="text-sm font-bold text-sky-200" data-id="element-286">
                      Watchdog Command Center
                    </p>
                    <p className="text-xs text-slate-400" data-id="element-287">
                      Live property overview
                    </p>
                  </div>
                  <div className="rounded-full bg-emerald-400/15 px-3 py-1 text-xs font-bold text-emerald-300" data-id="element-288">
                    All Systems Active
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2" data-id="element-289">
                  <div className="rounded-3xl border border-white/10 bg-black/30 p-4" data-id="element-290">
                    <div className="mb-4 flex items-center justify-between" data-id="element-291">
                      <div className="flex items-center gap-2 text-sm font-bold text-white" data-id="element-292">
                        <Camera className="h-4 w-4 text-sky-300" data-id="element-293" /> Live Cameras
                      </div>
                      <span className="text-xs text-slate-400" data-id="element-294">12 online</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2" data-id="element-295">
                      {['Front', 'Patio', 'Back', 'Lot'].map((item, index) => <div key={item} className="relative h-20 overflow-hidden rounded-2xl bg-gradient-to-br from-slate-700 to-slate-950" data-id="element-296">
                          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_25%,rgba(56,189,248,.45),transparent_32%)]" data-id="element-297" />
                          <div className="absolute bottom-2 left-2 rounded-full bg-black/50 px-2 py-1 text-[10px] font-bold text-white" data-id="element-298">
                            {item}
                          </div>
                          {index === 0 && <div className="absolute right-2 top-2 h-2 w-2 rounded-full bg-red-400" data-id="element-299" />}
                        </div>)}
                    </div>
                  </div>
                  <div className="rounded-3xl border border-white/10 bg-black/30 p-4" data-id="element-300">
                    <div className="mb-4 flex items-center gap-2 text-sm font-bold text-white" data-id="element-301">
                      <DoorOpen className="h-4 w-4 text-sky-300" data-id="element-302" /> Door Access
                    </div>
                    <div className="space-y-3" data-id="element-303">
                      {['Front Entry', 'Office', 'Back Door', 'Storage'].map((door, i) => <div key={door} className="flex items-center justify-between rounded-2xl bg-white/5 px-3 py-3" data-id="element-304">
                            <span className="text-xs text-slate-300" data-id="element-305">
                              {door}
                            </span>
                            <span className={`rounded-full px-2 py-1 text-[10px] font-bold ${i === 2 ? 'bg-amber-400/15 text-amber-200' : 'bg-emerald-400/15 text-emerald-300'}`} data-id="element-306">
                              {i === 2 ? 'Vendor' : 'Locked'}
                            </span>
                          </div>)}
                    </div>
                  </div>
                  <div className="rounded-3xl border border-white/10 bg-black/30 p-4" data-id="element-307">
                    <div className="mb-4 flex items-center gap-2 text-sm font-bold text-white" data-id="element-308">
                      <Activity className="h-4 w-4 text-sky-300" data-id="element-309" /> Activity
                      Alerts
                    </div>
                    <div className="space-y-3 text-xs text-slate-300" data-id="element-310">
                      <p className="rounded-2xl bg-white/5 p-3" data-id="element-311">
                        8:02 PM • System armed by manager
                      </p>
                      <p className="rounded-2xl bg-white/5 p-3" data-id="element-312">
                        8:16 PM • Back door opened for delivery
                      </p>
                      <p className="rounded-2xl bg-white/5 p-3" data-id="element-313">
                        8:18 PM • Door secured again
                      </p>
                    </div>
                  </div>
                  <div className="rounded-3xl border border-white/10 bg-sky-400 p-4 text-slate-950" data-id="element-314">
                    <div className="mb-4 flex items-center gap-2 text-sm font-black" data-id="element-315">
                      <ShieldCheck className="h-4 w-4" data-id="element-316" /> Property Status
                    </div>
                    <div className="space-y-3" data-id="element-317">
                      {[['Perimeter', '100%'], ['Camera Health', '98%'], ['Access Rules', '84%']].map(([label, val]) => <div key={label} data-id="element-318">
                          <div className="mb-1 flex justify-between text-xs font-bold" data-id="element-319">
                            <span data-id="element-320">{label}</span>
                            <span data-id="element-321">{val}</span>
                          </div>
                          <div className="h-2 rounded-full bg-slate-950/20" data-id="element-322">
                            <div className="h-2 rounded-full bg-slate-950" style={{
                          width: val
                        }} data-id="element-323" />
                          </div>
                        </div>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick stats strip */}
      <section className="relative z-10 -mt-12 px-4 sm:px-6 lg:px-8" data-id="element-324">
        <div className="mx-auto grid max-w-7xl gap-4 rounded-[2rem] border border-slate-200 bg-white p-4 shadow-2xl shadow-slate-200/70 sm:grid-cols-2 lg:grid-cols-4" data-id="element-325">
          {([[Building2, 'Commercial First', 'Built for businesses, properties, and multi site operations.', '/commercial'], [MapPinned, 'Local Service', 'Northern California walkthroughs, installs, and support.', '/about'], [ServerCog, 'Integrated Systems', 'Alarms, cameras, access control, and automation working together.', '/services'], [HomeIcon, 'Residential Ready', 'Smart home protection remains part of the full brand.', '/smart-home']] as const).map(([Icon, title, text, to]) => <Link key={title} to={to} className="group rounded-3xl bg-slate-50 p-6 transition hover:bg-slate-100" data-id="element-326">
              <Icon className="mb-4 h-7 w-7 text-sky-500" data-id="element-327" />
              <h3 className="font-black" data-id="element-328">{title}</h3>
              <p className="mt-2 text-sm leading-6 text-slate-600" data-id="element-329">{text}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-xs font-black text-sky-600 group-hover:gap-2" data-id="element-330">
                Learn more <ArrowRight className="h-3 w-3" data-id="element-331" />
              </span>
            </Link>)}
        </div>
      </section>

      {/* Services overview */}
      <section className="bg-white py-24" data-id="element-332">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-333">
          <div className="max-w-3xl" data-id="element-334">
            <SectionLabel data-id="element-335">Core solutions</SectionLabel>
            <h2 className="text-4xl font-black tracking-tight sm:text-5xl" data-id="element-336">
              One security partner for the whole property.
            </h2>
            <p className="mt-5 text-lg leading-8 text-slate-600" data-id="element-337">
              Every service is connected, modern, and easy to manage. Click any
              service to see how Watchdog handles it.
            </p>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-id="element-338">
            {services.map((service, index) => {
            const Icon = service.icon;
            return <motion.div key={service.slug} initial={{
              opacity: 0,
              y: 18
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.05
            }} data-id="element-339">
                  <Link to={service.slug === 'smart-home' ? '/smart-home' : `/services/${service.slug}`} className="group block h-full rounded-[2rem] border border-slate-200 bg-slate-50 p-7 transition hover:-translate-y-1 hover:bg-slate-950 hover:text-white hover:shadow-2xl" data-id="element-340">
                    <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-sky-500 shadow-sm group-hover:bg-sky-400 group-hover:text-slate-950" data-id="element-341">
                      <Icon className="h-7 w-7" data-id="element-342" />
                    </div>
                    <h3 className="text-2xl font-black" data-id="element-343">{service.title}</h3>
                    <p className="mt-3 leading-7 text-slate-600 group-hover:text-slate-300" data-id="element-344">
                      {service.short}
                    </p>
                    <span className="mt-5 inline-flex items-center gap-1 text-sm font-black text-sky-600 group-hover:text-sky-300 group-hover:gap-2" data-id="element-345">
                      Learn more <ArrowRight className="h-4 w-4" data-id="element-346" />
                    </span>
                  </Link>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Industries preview */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8" data-id="element-347">
        <div className="mx-auto max-w-3xl text-center" data-id="element-348">
          <SectionLabel data-id="element-349">Industry ready</SectionLabel>
          <h2 className="text-4xl font-black tracking-tight sm:text-5xl" data-id="element-350">
            Security designed around how your property actually works.
          </h2>
        </div>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-351">
          {industries.map(item => {
          const Icon = item.icon;
          return <Link key={item.slug} to={`/industries#${item.slug}`} className="group rounded-3xl border border-slate-200 bg-white p-6 transition hover:-translate-y-1 hover:border-sky-300 hover:shadow-xl" data-id="element-352">
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 group-hover:bg-sky-400 group-hover:text-slate-950" data-id="element-353">
                  <Icon className="h-6 w-6" data-id="element-354" />
                </div>
                <h3 className="text-lg font-black" data-id="element-355">{item.label}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600" data-id="element-356">
                  {item.headline}
                </p>
              </Link>;
        })}
        </div>
        <div className="mt-10 text-center" data-id="element-357">
          <CTAButton variant="dark" to="/industries" data-id="element-358">
            See all industries
          </CTAButton>
        </div>
      </section>

      {/* Process preview */}
      <section className="bg-slate-950 py-24 text-white" data-id="element-359">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-360">
          <div className="max-w-3xl" data-id="element-361">
            <SectionLabel dark data-id="element-362">Simple process</SectionLabel>
            <h2 className="text-4xl font-black tracking-tight sm:text-5xl" data-id="element-363">
              A clear path to a safer property.
            </h2>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4" data-id="element-364">
            {processSteps.map((item, index) => <motion.div key={item.step} initial={{
            opacity: 0,
            y: 18
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.06
          }} className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-7" data-id="element-365">
                <div className="mb-8 text-5xl font-black text-sky-400/40" data-id="element-366">
                  {item.step}
                </div>
                <h3 className="text-xl font-black" data-id="element-367">{item.title}</h3>
                <p className="mt-4 leading-7 text-slate-300" data-id="element-368">{item.text}</p>
              </motion.div>)}
          </div>
          <div className="mt-10" data-id="element-369">
            <CTAButton to="/process" variant="secondary" data-id="element-370">
              See the full process
            </CTAButton>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-sky-50 py-20" data-id="element-371">
        <div className="mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8" data-id="element-372">
          <SectionLabel data-id="element-373">Free assessment</SectionLabel>
          <h2 className="text-4xl font-black tracking-tight sm:text-5xl" data-id="element-374">
            Know what your property needs before you spend a dollar.
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-slate-600" data-id="element-375">
            Tell us about your property and a Watchdog specialist will design
            and price a system tailored to it — no pressure, no scripts.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3" data-id="element-376">
            <CTAButton to="/assessment" data-id="element-377">Get my free assessment</CTAButton>
            <CTAButton variant="dark" href="tel:+19168424729" data-id="element-378">
              Call (916) 842-4729
            </CTAButton>
          </div>
        </div>
      </section>
    </>;
}